import { makeAutoObservable } from "mobx";

const EMPTY_DATA = { summary: {}, warnings: [], baselineYear: 2019 };
const SUMMARY_KEYS = ["total", "scope1", "scope2", "scope3"];

const toFiniteYear = (value) => {
  const year = Number(value);
  return Number.isFinite(year) ? year : null;
};

class DashboardEChartsTotalsStore {
  data = EMPTY_DATA;
  loading = false;
  hasLoadedOnce = false;
  requestSeq = 0;

  // KPI-strip decoupling. The trend range drives the main totals request,
  // but the strip's selected year may sit outside that range; such years are
  // fetched individually and merged here. `contextKey` fingerprints the
  // filter context (frameworks + reporting periods) a summary belongs to —
  // extras from a different context are never mixed into the current one.
  contextKey = "";
  rangeYears = [];
  extraSummaryByYear = {};
  extraWarningsByYear = {};
  extraYears = [];
  pendingExtraYears = [];

  constructor() {
    makeAutoObservable(this);
  }

  nextRequestSeq = () => {
    this.requestSeq += 1;
    return this.requestSeq;
  };

  isStaleSeq = (seq) => seq !== this.requestSeq;

  setData = (response, { contextKey = "", requestedYears = [] } = {}) => {
    this.data = response ?? EMPTY_DATA;
    this.hasLoadedOnce = true;
    const years = requestedYears.map(toFiniteYear).filter((y) => y != null);
    if (contextKey !== this.contextKey) {
      // New filter context invalidates every extra, including in-flight ones:
      // their merges are context-guarded anyway, and a stale pending flag
      // must not suppress a refetch under the new context.
      this.contextKey = contextKey;
      this.extraSummaryByYear = {};
      this.extraWarningsByYear = {};
      this.extraYears = [];
      this.pendingExtraYears = [];
    } else {
      // Same context: the range response is now authoritative for the years
      // it covered, so drop overlapping extras entirely (an extra must not
      // shadow a year the newer range response omitted).
      this.pruneExtras(years);
    }
    this.rangeYears = years;
  };

  pruneExtras = (years = []) => {
    const covered = new Set(years.map(toFiniteYear).filter((y) => y != null));
    if (covered.size === 0) return;
    this.extraYears = this.extraYears.filter((y) => !covered.has(y));
    const keep = ([key]) => !covered.has(Number(key));
    this.extraSummaryByYear = Object.fromEntries(
      Object.entries(this.extraSummaryByYear).filter(keep),
    );
    this.extraWarningsByYear = Object.fromEntries(
      Object.entries(this.extraWarningsByYear).filter(keep),
    );
  };

  // Record a strip-fetched year. An empty result still marks the year as
  // attempted (`extraYears`) so a data-less year cannot refetch forever.
  mergeExtraYear = (year, { summary = null, warnings = [] }, contextKey) => {
    if (contextKey !== this.contextKey) return;
    const n = toFiniteYear(year);
    if (n == null) return;
    // A range response that covered this year is authoritative, even when it
    // omitted the year's key; a slower strip fetch that started earlier must
    // not re-add what the newer range response pruned.
    if (this.rangeYears.includes(n)) return;
    if (!this.extraYears.includes(n)) this.extraYears.push(n);
    if (summary != null) {
      this.extraSummaryByYear = {
        ...this.extraSummaryByYear,
        [String(n)]: summary,
      };
    }
    if (Array.isArray(warnings) && warnings.length > 0) {
      this.extraWarningsByYear = {
        ...this.extraWarningsByYear,
        [String(n)]: warnings,
      };
    }
  };

  markExtraPending = (year) => {
    const n = toFiniteYear(year);
    if (n == null || this.pendingExtraYears.includes(n)) return;
    this.pendingExtraYears.push(n);
  };

  // Context-guarded: a late finally-clear from a fetch that started under an
  // older context must not remove the same year's pending flag that a newer
  // context legitimately set.
  clearExtraPending = (year, contextKey) => {
    if (contextKey !== this.contextKey) return;
    const n = toFiniteYear(year);
    this.pendingExtraYears = this.pendingExtraYears.filter((y) => y !== n);
  };

  setLoading = (value) => {
    this.loading = Boolean(value);
  };

  reset = () => {
    this.data = EMPTY_DATA;
    this.loading = false;
    this.hasLoadedOnce = false;
    this.contextKey = "";
    this.rangeYears = [];
    this.extraSummaryByYear = {};
    this.extraWarningsByYear = {};
    this.extraYears = [];
    this.pendingExtraYears = [];
  };

  // Range response wins over strip extras on overlap: both describe the same
  // context, but the range payload is the newer fetch whenever they collide
  // (and setData prunes covered extras, so overlap is transient).
  get summary() {
    return { ...this.extraSummaryByYear, ...(this.data?.summary ?? {}) };
  }
  get warnings() {
    return this.data?.warnings ?? [];
  }

  // Warnings from a strip fetch stay keyed by year and are surfaced only for
  // the year actually displayed; flattening every cached extra's warnings
  // would keep stale banners alive after the selection moves on.
  extraWarningsFor = (year) => {
    if (year == null) return [];
    return this.extraWarningsByYear[String(year)] ?? [];
  };
  get baselineYear() {
    return this.data?.baselineYear ?? 2019;
  }

  // Loaded means "a request for this year completed in the current context",
  // NOT "the summary has a key for it" — the API omits years that have no
  // reporting boundary, and treating those as unloaded would loop forever.
  // The baseline year is server-included in every totals response, so it
  // counts as loaded from the first response onward.
  get loadedYears() {
    const base = this.hasLoadedOnce ? [this.baselineYear] : [];
    return [...new Set([...base, ...this.rangeYears, ...this.extraYears])];
  }

  isYearLoaded = (year) => {
    const n = toFiniteYear(year);
    return n != null && this.loadedYears.includes(n);
  };

  // KPI strip reader — `key` is one of "scope1" | "scope2" | "scope3" | "total".
  // Returns null when the year or key is missing / poisoned.
  cellFor = (year, key) => {
    if (year == null) return null;
    const yearCell = this.summary?.[String(year)];
    if (!yearCell || !Object.prototype.hasOwnProperty.call(yearCell, key)) {
      return null;
    }
    return yearCell[key];
  };

  // A year has data when any KPI cell holds a finite number — missing and
  // poisoned (null) cells don't count.
  hasDataForYear = (year) => {
    if (year == null) return false;
    const yearCell = this.summary?.[String(year)];
    if (!yearCell) return false;
    return SUMMARY_KEYS.some((key) => Number.isFinite(yearCell[key]));
  };

  // Newest candidate year with any totals data; null when no year has data.
  latestYearWithData = (years = []) => {
    const sorted = [...years]
      .map(Number)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);
    for (let i = sorted.length - 1; i >= 0; i -= 1) {
      if (this.hasDataForYear(sorted[i])) return sorted[i];
    }
    return null;
  };
}

export const dashboardEChartsTotalsStore = new DashboardEChartsTotalsStore();
export { DashboardEChartsTotalsStore };
